class JournalStoryV2 extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-journal-content]');
    this.progress = this.querySelector('[data-journal-progress]');
    if (!this.content) return;

    this.headings = [...this.content.querySelectorAll('h2')];
    this.buildChapters();
    this.insertQuestionIntroImage();
    this.insertStoryImages();
    this.markImageTreatments();
    this.linkQuestionProductImages();
    this.decorateChapters();

    this.onScroll = this.update.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  insertStoryImages() {
    let imageUrl = this.dataset.wundenHiddenStone;
    if (!imageUrl) return;
    if (imageUrl.includes('Wunden%20I%20Silver%20signet%20ring%20Hidden%20stone_')) {
      imageUrl = imageUrl
        .replace(/Wunden%20I%20Silver%20signet%20ring%20Hidden%20stone_\.png/i, 'wunden-i-silver-signet-ring-hidden-stone.png')
        .replace(/\?v=\d+$/, '');
    }

    const heading = this.headings.find((item) => {
      const text = item.textContent.trim().toLowerCase();
      return text.includes('secret stone underneath') || text.includes('stone underneath');
    });
    if (!heading) return;

    const chapterInner = heading.closest('.journal-chapter__inner');
    if (!chapterInner || chapterInner.querySelector('[data-wunden-hidden-stone]')) return;

    const paragraphs = [...chapterInner.children].filter((item) => item.tagName === 'P');
    const anchor = paragraphs[1] || paragraphs[0] || heading;
    const figure = document.createElement('figure');
    figure.className = 'journal-story__standalone-image';
    figure.dataset.wundenHiddenStone = '';

    const image = document.createElement('img');
    image.src = imageUrl;
    image.loading = 'lazy';
    image.alt = 'Wunden I silver signet ring showing the hidden tsavorite beneath the signet face';
    image.title = 'Wunden I silver signet ring with a hidden tsavorite';

    const caption = document.createElement('figcaption');
    caption.textContent = 'Wunden I silver signet ring with a hidden tsavorite beneath the signet face';

    figure.append(image, caption);
    anchor.insertAdjacentElement('afterend', figure);
  }

  insertQuestionIntroImage() {
    if (!this.classList.contains('journal-story--questions')) return;
    const imageUrl = this.dataset.questionIntroImage;
    const intro = this.content.querySelector('.journal-story__intro');
    if (!imageUrl || !intro || intro.querySelector('[data-question-intro-image]')) return;

    const paragraphs = [...intro.children].filter((item) => item.tagName === 'P');
    const anchor = paragraphs[2] || paragraphs[1] || paragraphs[0];
    if (!anchor) return;

    const figure = document.createElement('figure');
    figure.className = 'journal-story__standalone-image';
    figure.dataset.questionIntroImage = '';

    const image = document.createElement('img');
    image.src = imageUrl;
    image.loading = 'lazy';
    image.alt = 'Diamond halo engagement ring worn on the hand';
    image.title = 'Diamond halo engagement ring worn on the hand';

    const link = document.createElement('a');
    link.href = '/products/british-engagement-ring-cannele';
    link.className = 'journal-story__product-image-link';
    link.setAttribute('aria-label', 'View the Cannelé engagement ring');

    const caption = document.createElement('figcaption');
    caption.textContent = 'CANNELÉ ENGAGEMENT RING BY ANDREW GEOGHEGAN';

    link.appendChild(image);
    figure.append(link, caption);
    anchor.insertAdjacentElement('afterend', figure);
  }

  linkQuestionProductImages() {
    if (!this.classList.contains('journal-story--questions')) return;

    const products = [
      { match: 'Asteria_Sapphire', href: '/products/sapphire-ring-for-engagement', label: 'View the Asteria Sapphire Halo Ring' },
      { match: 'Cannele-sapphire-ring-box-alan', href: '/products/british-engagement-ring-cannele', label: 'View the Cannelé engagement ring' },
      { match: 'satellite-bridal', href: '/products/satellite-diamond', label: 'View the Satellite Diamond Engagement Ring' }
    ];

    this.content.querySelectorAll('img').forEach((image) => {
      if (image.closest('a')) return;
      const product = products.find((item) => image.src.includes(item.match));
      if (!product) return;

      const link = document.createElement('a');
      link.href = product.href;
      link.className = 'journal-story__product-image-link';
      link.setAttribute('aria-label', product.label);
      image.parentNode.insertBefore(link, image);
      link.appendChild(image);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  buildChapters() {
    const firstHeading = this.headings[0];
    if (!firstHeading) return;

    const intro = document.createElement('div');
    intro.className = 'journal-story__intro';
    while (this.content.firstChild && this.content.firstChild !== firstHeading) {
      intro.appendChild(this.content.firstChild);
    }
    this.content.insertBefore(intro, firstHeading);

    this.headings.forEach((heading, index) => {
      const chapter = document.createElement('section');
      chapter.className = `journal-chapter journal-chapter--${(index % 4) + 1}`;
      const inner = document.createElement('div');
      inner.className = 'journal-chapter__inner';
      heading.parentNode.insertBefore(chapter, heading);
      chapter.appendChild(inner);
      inner.appendChild(heading);

      let node = chapter.nextSibling;
      while (node && !(node.nodeType === 1 && node.tagName === 'H2')) {
        const next = node.nextSibling;
        inner.appendChild(node);
        node = next;
      }
    });
  }

  markImageTreatments() {
    this.content.querySelectorAll('div').forEach((container) => {
      const directFigures = [...container.children].filter((child) => child.tagName === 'FIGURE');
      const onlyFigures = [...container.children].every((child) => child.tagName === 'FIGURE');
      if (directFigures.length > 1 && onlyFigures) container.classList.add('journal-story__image-pair');
    });

    this.content.querySelectorAll('figure').forEach((figure) => {
      if (!figure.closest('.journal-story__image-pair')) {
        figure.classList.add('journal-story__standalone-image');
      }

      const image = figure.querySelector('img');
      if (!image) return;

      const applyRatio = () => {
        if (image.naturalWidth && image.naturalHeight && image.naturalWidth / image.naturalHeight < 0.86) {
          figure.classList.add('journal-story__narrow-editorial-image');
        }
      };

      if (image.complete) applyRatio();
      else image.addEventListener('load', applyRatio, { once: true });
    });

    this.content.querySelectorAll('p').forEach((paragraph) => {
      if (paragraph.querySelector(':scope > img') && !paragraph.closest('.journal-story__image-pair')) {
        paragraph.classList.add('journal-story__standalone-image');
      }
    });
  }

  decorateChapters() {
    this.headings.forEach((heading, index) => {
      if (this.classList.contains('journal-story--questions')) {
        if (index < 10) heading.dataset.chapter = `Question ${String(index + 1).padStart(2, '0')}`;
        else if (index === 10) heading.dataset.chapter = 'Next steps';
        else heading.dataset.chapter = 'Useful answers';
        return;
      }

      heading.dataset.chapter = `Chapter ${String(index + 1).padStart(2, '0')}`;
    });
  }

  update() {
    const rect = this.getBoundingClientRect();
    const total = Math.max(this.offsetHeight - window.innerHeight, 1);
    const progress = Math.min(Math.max(-rect.top / total, 0), 1);
    if (this.progress) this.progress.style.width = `${progress * 100}%`;
  }
}

if (!customElements.get('journal-story-v2')) {
  customElements.define('journal-story-v2', JournalStoryV2);
}
